import { useState } from 'react';
import { AlertBanner, Badge, Button, Dialog, Input, LanguageSwitcher, Toast } from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import { ApiError, endpoints } from '../state/api.js';
import { useSession } from '../state/session.js';
import { useResource } from '../lib/useResource.js';
import { Screen } from '../components/Screen.js';
import { formatDateTime } from '../lib/format.js';

/**
 * AKUN & NOTIFIKASI — Figma `116:1635` · M01 · rute `/settings/account` · izin pemilik akun.
 *
 * PETA §4 SENGAJA TIDAK MEMBERI LAYAR INI SLOT NAVIGASI: ia dicapai lewat blok pengguna di
 * kaki bilah sisi. Itu diikuti di sini — `navigasi.test.ts` menghitungnya sebagai salah
 * satu dari tiga pintu tanpa-slot-nav, dan `BUILT_ROUTES` memuatnya tanpa butir nav.
 *
 * APA YANG BEKERJA HARI INI, DAN APA YANG BELUM — DINYATAKAN, TIDAK DISEMBUNYIKAN
 * ------------------------------------------------------------------------------
 * Peta §5.7 menuntut empat hal: simpan profil · ubah preferensi notifikasi · ganti bahasa ·
 * keluarkan sesi.
 *
 *   · **Profil** dan **bahasa**: `PATCH /v1/me` ada dan bekerja. Bahasa berlaku SEKETIKA,
 *     tanpa memuat ulang halaman, dan tersimpan di PROFIL — bukan di peramban — sehingga
 *     ia ikut berpindah perangkat (peta §7).
 *   · **Sesi**: `GET /v1/me/sessions` dan `DELETE /v1/me/sessions/{id}` ada. Sesi yang
 *     sedang berjalan TIDAK dapat dikeluarkan dari sini (`CANNOT_REVOKE_CURRENT_SESSION`),
 *     dan tombolnya karena itu tidak ada pada barisnya — bukan mati.
 *   · **Preferensi notifikasi**: `GET/PUT /v1/me/notification-preferences` BELUM punya
 *     handler; keduanya menjawab `501`. Layar ini karena itu menyatakan aturan yang
 *     BERLAKU — jam tenang 20.00–06.00, dan eskalasi keselamatan yang selalu dikirim
 *     seketika — dan mengatakan bahwa pengaturannya belum dapat diubah dari sini. Sebuah
 *     formulir yang menyimpan ke rute 501 akan kehilangan isian orang tanpa mengatakannya.
 */
export interface AccountSettingsProps {
  /** Tautan `Kelola` ke `/settings/account/privacy` (`188:1225`). */
  onOpenPrivacy?: () => void;
}

export function AccountSettings({ onOpenPrivacy }: AccountSettingsProps) {
  const { t, locale, me, setLocale, translator } = useSession();
  const sessions = useResource(async () => (await endpoints.mySessions()).items, []);
  /**
   * Ringkasan privasi dibaca TERPISAH dari sesi, dan kegagalannya tidak menjatuhkan layar:
   * seseorang yang datang ke Pengaturan Akun untuk mencabut sebuah perangkat tidak boleh
   * kehilangan seluruh layar karena panel ringkas privasi gagal dimuat.
   */
  const privasi = useResource(async () => endpoints.consents(), []);

  const [jobTitle, setJobTitle] = useState(me?.job_title ?? '');
  const [busy, setBusy] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const kotor = (me?.job_title ?? '') !== jobTitle;

  async function simpanProfil() {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.patchMe({ job_title: jobTitle.trim() === '' ? null : jobTitle.trim() });
      setToast(t('account.saved'));
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function keluarkan(id: string) {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.revokeSession(id);
      setToast(t('account.session.revoked'));
      setRevoking(null);
      await sessions.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen title={t('account.title')} subtitle={t('account.subtitle')} resource={sessions}>
      {(items) => (
        <>
          {problem ? (
            <AlertBanner
              kind="danger"
              signalWord={t('signal.danger')}
              title={renderProblem(translator, problem.problem)}
            />
          ) : null}

          <section className="card">
            <h2 className="card__title">{t('account.profile')}</h2>
            <dl className="facts">
              <div>
                <dt>{t('users.column.person')}</dt>
                <dd>{me?.full_name ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('users.field.email')}</dt>
                <dd>{me?.email ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('users.column.employeeNo')}</dt>
                <dd>{me?.employee_no ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('users.column.role')}</dt>
                <dd>{me?.role_code ? t(`role.${me.role_code}`) : (me?.actor_kind ?? '—')}</dd>
              </div>
            </dl>
            <Input
              label={t('userDetail.jobTitle')}
              value={jobTitle}
              maxLength={120}
              onChange={(e) => setJobTitle(e.target.value)}
            />
            <p className="page__actions">
              {/*
                Tombol simpan AKTIF hanya bila ada perubahan (kondisi peta §5.7: "ada
                perubahan belum disimpan"). Sebuah tombol simpan yang selalu aktif tidak
                memberi tahu apakah ada yang berubah.
              */}
              <Button variant="primary" busy={busy} disabled={!kotor} onClick={() => void simpanProfil()}>
                {t('app.save')}
              </Button>
            </p>
          </section>

          <section className="card">
            <h2 className="card__title">{t('account.language')}</h2>
            <p className="card__muted">{t('account.language.note')}</p>
            {/*
              `LanguageSwitcher` (`164:31`) menggantikan dua tombol `.filters` buatan
              sendiri. Bentuk dua tombolnya DIPERTAHANKAN — itu yang benar untuk tepat dua
              pilihan, dan Figma yang menyusul ke kode (D-05, satu-satunya temuan di mana
              arahnya begitu). Yang berubah adalah bentuk pilnya, ikon globenya, dan
              semantik `role="radiogroup"` yang membuat pembaca layar membacakan keduanya
              sebagai satu pilihan alih-alih dua tombol tak berhubungan.
            */}
            <LanguageSwitcher
              label={t('account.language')}
              value={locale === 'en-US' ? 'en-US' : 'id-ID'}
              onChange={(next) => void setLocale(next)}
            />
          </section>

          <section className="card">
            <h2 className="card__title">{t('account.notifications')}</h2>
            {/* Aturan yang BERLAKU, dinyatakan. Pengaturannya belum dapat diubah di sini. */}
            <p>{t('account.quietHours')}</p>
            <p>{t('account.safetyAlways')}</p>
            <AlertBanner
              kind="information"
              signalWord={t('signal.information')}
              title={t('account.notifications.pending')}
            />
          </section>

          {/*
            PANEL RINGKAS `Privacy and your data` — Figma `186:1228` (EN) / `192:1431` (ID).
            DUA BARIS dan SATU tautan, tidak lebih: isi nyatanya ada di
            `/settings/account/privacy` (`188:1225`), dan memerasnya ke sini akan
            memperkecil justru bagian yang paling bermakna secara hukum — riwayat
            append-only, tempat terlihat kapan seseorang berubah pikiran.

            `Quiet hours` TIDAK dipindahkan oleh perbaikan ini, dan itu perlu dicatat agar
            tidak dicari: perancang memindahkannya dari rel kanan ke kaki panel notifikasi
            di FIGMA, dan di KODE ia sudah berada di sana sejak layar ini dibangun
            (`t('account.quietHours')` di dalam panel notifikasi di atas). Tidak ada
            pekerjaan kode untuk butir itu.
          */}
          <section className="card">
            <h2 className="card__title">{t('account.privacyPanel.title')}</h2>
            {privasi.data ? (
              <>
                <p>
                  {(() => {
                    const versi = privasi.data.items.find((r) => r.notice_version !== null);
                    return versi
                      ? t('account.privacyPanel.noticeVersion', { version: versi.notice_version })
                      : t('account.privacyPanel.noticeUnknown');
                  })()}
                </p>
                <p className="card__muted">
                  {t('account.privacyPanel.consentCount', { count: privasi.data.items.length })}
                </p>
              </>
            ) : null}
            <p className="page__actions">
              <Button variant="secondary" onClick={() => onOpenPrivacy?.()}>
                {t('account.privacyPanel.manage')}
              </Button>
            </p>
          </section>

          <section className="card">
            <h2 className="card__title">{t('account.sessions')}</h2>
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('account.session.device')}</th>
                  <th scope="col">{t('account.session.lastSeen')}</th>
                  <th scope="col">{t('locations.column.action')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id}>
                    <td>
                      {s.device_label ?? t('account.session.unknownDevice')}
                      {s.current ? (
                        <>
                          {' '}
                          <Badge badgeTone="INFORMATION">{t('account.session.current')}</Badge>
                        </>
                      ) : null}
                    </td>
                    <td>
                      {s.last_seen_at
                        ? formatDateTime(s.last_seen_at, locale)
                        : formatDateTime(s.created_at, locale)}
                    </td>
                    <td>
                      {s.current ? (
                        /* Sesi berjalan tidak dapat dikeluarkan dari sini — kondisi peta
                           §5.7. Tombolnya TIDAK ADA, bukan mati: sebuah tombol mati
                           mengajak orang mencobanya lalu membaca galat. */
                        <span className="card__muted">{t('account.session.signOutInstead')}</span>
                      ) : (
                        <Button variant="text" onClick={() => setRevoking(s.id)}>
                          {t('account.session.revoke')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <Dialog
            open={revoking !== null}
            kind="confirm"
            title={t('account.session.revoke')}
            description={t('account.session.revoke.describe')}
            confirmLabel={t('account.session.revoke')}
            cancelLabel={t('app.cancel')}
            busy={busy}
            onConfirm={() => {
              if (revoking) void keluarkan(revoking);
            }}
            onCancel={() => setRevoking(null)}
          />

          {toast ? (
            <div className="assist-toast-stack">
              <Toast
                kind="success"
                signalWord={t('signal.success')}
                title={toast}
                onDismiss={() => setToast(null)}
                closeLabel={t('app.dismiss')}
              />
            </div>
          ) : null}
        </>
      )}
    </Screen>
  );
}
